import React, { useState } from 'react';
import { statusFlow, failurePaths, failureCodes, getNextStepLabel } from '../utils/statusUtils';

const StatusUpdateForm = ({ phone, user, onUpdateStatus }) => {
  const [result, setResult] = useState('pass');
  const [failureCode, setFailureCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Verificar permisos
  const canUpdate =
    user.role === 'admin' ||
    user.role === 'supervisor' ||
    (user.role === 'technician' && user.assigned_step === phone.status);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canUpdate) {
      alert('No tienes permiso para actualizar este paso.');
      return;
    }

    if (result === 'fail' && !failureCode) {
      alert('Por favor seleccione un código de falla');
      return;
    }

    setIsSubmitting(true);

    let newStatus;
    if (result === 'fail') {
      newStatus = failurePaths[phone.status];
    } else {
      if (phone.status === 'Phone Rework') {
        const lateFailureSteps = ['Matching Kit For Assembly', 'Phone Closing', 'Quality Inspection', 'Oleophobic And Overlay', 'Packaging'];
        const lastFailedStep = phone.history
          .slice()
          .reverse()
          .find(h => 
            lateFailureSteps.includes(h.status) && h.result === 'fail'
          );
        newStatus = lastFailedStep ? lastFailedStep.status : statusFlow[statusFlow.indexOf(phone.status) + 1];
      } else if (phone.status === 'Warehouse Scrap') {
        const earlyFailureSteps = ['Receiving', 'Incoming', 'Opening', 'LCD Buffing'];
        const lastFailedStep = phone.history
          .slice()
          .reverse()
          .find(h => 
            earlyFailureSteps.includes(h.status) && h.result === 'fail'
          );
        newStatus = lastFailedStep ? lastFailedStep.status : statusFlow[statusFlow.indexOf(phone.status) + 1];
      } else {
        newStatus = statusFlow[statusFlow.indexOf(phone.status) + 1];
      }
    }

    try {
      const response = await fetch('backend/phones.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          imei: phone.imei,
          status: newStatus,
          history: [
            ...phone.history,
            {
              status: phone.status,
              result,
              comment: result === 'fail' ? failureCode : '',
              user: user.name,
              timestamp: new Date().toISOString()
            }
          ],
        }),
      });
      const data = await response.json();
      if (data.success) {
        const fetchUpdated = await fetch(`backend/phones.php?imei=${phone.imei}`, { credentials: 'include' });
        const result = await fetchUpdated.json();
        if (result.phone) {
          onUpdateStatus(result.phone);
        } else {
          console.error('No se pudo actualizar el estado del teléfono');
        }
      } else {
        alert(data.error || 'Error al actualizar estado');
      }

    } catch (error) {
      console.error('Error al actualizar estado:', error);
    } finally {
      setResult('pass');
      setFailureCode('');
      setIsSubmitting(false);
    }
  };

  const nextStepLabel = getNextStepLabel(phone.status);
  const failureActionLabel = phone.status === 'Phone Rework' ? 'Enviar a Rework' : 'Registrar Falla';

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Cambiar Estado</h2>

      {!canUpdate ? (
        <p className="text-red-600 font-medium">Este dispositivo no está en tu paso. No puedes actualizar su estado.</p>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Resultado</label>
            <div className="mt-1 space-y-2">
              <div className="flex items-center">
                <input
                  id="pass"
                  name="result"
                  type="radio"
                  value="pass"
                  checked={result === 'pass'}
                  onChange={() => setResult('pass')}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300"
                />
                <label htmlFor="pass" className="ml-2 block text-sm text-gray-700">
                  {nextStepLabel}
                </label>
              </div>
              {phone.status !== 'Warehouse Scrap' && phone.status !== 'Finish Good' && (
                <div className="flex items-center">
                  <input
                    id="fail"
                    name="result"
                    type="radio"
                    value="fail"
                    checked={result === 'fail'}
                    onChange={() => setResult('fail')}
                    className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300"
                  />
                  <label htmlFor="fail" className="ml-2 block text-sm text-gray-700">
                    {failureActionLabel}
                  </label>
                </div>
              )}
            </div>
          </div>
          {result === 'fail' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Seleccionar Motivo de Falla</label>
              <select
                value={failureCode}
                onChange={(e) => setFailureCode(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:focus:ring-[#31a749] focus:border-[#31a749]"
                required
              >
                <option value="">Seleccione un código</option>
                {failureCodes.map(({code, description}) => (
                  <option key={code} value={`${code}: ${description}`}>
                    {code} - {description}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            type="submit"
            disabled={isSubmitting || (result === 'fail' && !failureCode)}
            className={`w-full flex justify-center items-center py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              result === 'pass' 
                ? 'bg-[#31a749] hover:bg-green-700 focus:ring-green-500 text-white' 
                : 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white'
            } ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {result === 'pass' ? 'Actualizando...' : 'Reportando...'}
              </>
            ) : result === 'pass' ? nextStepLabel : failureActionLabel}
          </button>
        </form>
      )}
    </div>
  );
};

export default StatusUpdateForm;
